<template>
  <div v-if="authIsReady">
    <UnifiedHeader />
    <main class="max-w-4xl mx-auto">
      <div class="bg-white shadow-md rounded-xl p-6">
        <h1 class="text-2xl font-bold text-gray-800 mb-6 text-center">Edit Your Profile</h1>
        <form @submit.prevent="updateProfile" class="space-y-6">
          <div>
            <label class="block text-sm font-medium text-gray-700">Profile Image</label>
            <div class="mt-2 flex items-center space-x-4">
              <img v-if="photoURL" :src="photoURL" class="h-16 w-16 rounded-full object-cover">
              <div v-else class="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-500 text-2xl">{{ displayNameInput ? displayNameInput.charAt(0).toUpperCase() : '' }}</div>
              <input type="file" @change="handleFileSelect" accept="image/png, image/jpeg" class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-brand-blue/10 file:text-brand-purple hover:file:bg-brand-blue/20">
            </div>
          </div>
          <div>
            <label for="displayName" class="block text-sm font-medium text-gray-700">Display Name</label>
            <input v-model="displayNameInput" type="text" id="displayName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple">
          </div>
          <div class="pt-4">
            <button type="submit" :disabled="isProcessing" class="w-full bg-brand-pink text-white py-2 px-4 rounded-md hover:opacity-90 disabled:opacity-50">
              <span v-if="isProcessing">Saving...</span><span v-else>Save Changes</span>
            </button>
          </div>
        </form>
        <div v-if="feedbackMessage" class="mt-4 p-3 rounded-md text-sm" :class="isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'"><p>{{ feedbackMessage }}</p></div>
      </div>
    </main>
    <footer class="mt-12 text-center py-4 space-y-4">
      <div class="flex items-center justify-center space-x-2"><img src="@/assets/logo.png" alt="Castive Logo" class="h-6 w-auto opacity-75"><p class="text-sm text-gray-600 font-medium">Powered by Castive</p></div>
      <div class="md:hidden"><a href="https://mannmadecreations.co.uk" target="_blank" rel="noopener noreferrer" class="pixel-perfect-link">GET PIXEL PERFECTION</a></div>
    </footer>
  </div>
</template>
<script>
import { storage, auth } from '@/firebase';
import { updateProfile } from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { mapState } from 'vuex';
import UnifiedHeader from '@/components/UnifiedHeader.vue';

export default {
  name: 'ProfileView',
  components: { UnifiedHeader },
  data() {
    return {
      displayNameInput: '', photoURL: '', selectedFile: null,
      isProcessing: false, feedbackMessage: '', isError: false,
    }
  },
  computed: {
    ...mapState(['user', 'authIsReady']),
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.selectedFile = file;
      this.photoURL = URL.createObjectURL(file);
    },
    async updateProfile() {
      if (!this.user) {
        this.feedbackMessage = "You must be logged in to update your profile.";
        this.isError = true;
        return;
      }
      this.isProcessing = true;
      this.feedbackMessage = '';
      this.isError = false;
      try {
        let newPhotoURL = this.user.photoURL;
        if (this.selectedFile) {
          const fileExtension = this.selectedFile.name.split('.').pop();
          const fileName = `profile-images/${this.user.uid}/${uuidv4()}.${fileExtension}`;
          const storageRef = ref(storage, fileName);
          await uploadBytes(storageRef, this.selectedFile);
          newPhotoURL = await getDownloadURL(storageRef);
        }
        await updateProfile(auth.currentUser, {
          displayName: this.displayNameInput,
          photoURL: newPhotoURL,
        });
        this.$store.dispatch('initializeAuth');
        this.feedbackMessage = "Profile updated successfully!";
      } catch (error) {
        console.error("Error updating profile:", error);
        this.feedbackMessage = "Failed to update profile. Please try again.";
        this.isError = true;
      } finally {
        this.isProcessing = false;
      }
    },
    setInitialData() {
      if (this.user) {
        this.displayNameInput = this.user.displayName || '';
        this.photoURL = this.user.photoURL || '';
      }
    }
  },
  watch: {
    user: {
      handler: 'setInitialData',
      immediate: true
    }
  }
}
</script>